import React, { useState, useRef, useCallback } from 'react';
import { Turnstile as CFTurnstile } from '@marsidev/react-turnstile';
import styled from 'styled-components';

// This can be used to import the test keys
// To test the turnstile in a interactive way
// Set siteKey={TURNSTILE_TEST_SITE_KEYS.FORCES_INTERACTIVE}
// import { TURNSTILE_TEST_SITE_KEYS } from 'shared/constants/turnstile';
import type { TurnstileAction } from 'shared/constants/turnstile';

import PublicEnv from 'shared/utils/public-env';

// Default time to wait for the Turnstile script/widget to call back (success or error) before
// treating it as failed. See executeAndWaitForToken for why this bound exists.
export const TURNSTILE_TIMEOUT_MS = 10000;

export type TurnstileProps = {
  onSuccess?: (token: string) => void;
  onError?: (errorMsg: string) => void;
  // Invoked once Cloudflare has actually rendered an interactive checkbox for the user. Lets
  // callers distinguish "still loading/probably broken" from "a real person is now solving it",
  // since Turnstile can escalate to an interactive challenge for any invocation regardless of
  // the appearance setting (appearance only controls widget visibility styling).
  onBeforeInteractive?: () => void;
  size?: 'compact' | 'flexible' | 'normal';
  action: TurnstileAction;
  appearance?: 'always' | 'interaction-only';
};

const Turnstile: React.FC<TurnstileProps> = ({
  onSuccess,
  onError,
  onBeforeInteractive,
  action,
  appearance = 'always',
  size = 'normal',
}): JSX.Element => {
  const [turnstileIsInteractive, setturnstileIsInteractive] = useState<boolean>(appearance !== 'interaction-only');
  return (
    <TurnstileContainer $turnstileIsInteractive={turnstileIsInteractive}>
      <CFTurnstile
        siteKey={PublicEnv.turnstileSiteKey}
        // siteKey={TURNSTILE_TEST_SITE_KEYS.FORCES_INTERACTIVE}
        onBeforeInteractive={() => {
          setturnstileIsInteractive(true);
          onBeforeInteractive?.();
        }}
        onSuccess={(token) => {
          onSuccess?.(token);
        }}
        onError={(errorMsg) => {
          console.error('Turnstile error', errorMsg);
          onError?.(errorMsg);
        }}
        // Cloudflare's own signal that an interactive challenge was presented but not solved in
        // time. This is the correct way to bound a challenge the user is actively (or was)
        // engaging with - unlike our own external safety timer below, Cloudflare's internal
        // timeout accounts for interactive-solve time rather than firing on a blind fixed clock.
        onTimeout={() => {
          const errorMsg = 'Turnstile challenge timed out';
          console.error('Turnstile error', errorMsg);
          onError?.(errorMsg);
        }}
        // Without this, a failed/blocked load of challenges.cloudflare.com/turnstile/v0/api.js
        // (network egress issues, ad blockers, etc.) never fires onload *or* onerror on the
        // injected <script> tag, so the widget never renders and neither onSuccess nor onError
        // above ever gets called - leaving executeAndWaitForToken() (and the caller's submit
        // spinner) hung forever.
        scriptOptions={{
          onError: () => {
            const errorMsg = 'Failed to load Turnstile script';
            console.error('Turnstile error', errorMsg);
            onError?.(errorMsg);
          },
        }}
        options={{
          action,
          size,
          appearance,
          theme: 'light',
          feedbackEnabled: false,
        }}
      />
    </TurnstileContainer>
  );
};

Turnstile.displayName = 'Turnstile';

/**
 * Custom hook that provides a Turnstile component instance along with a method to execute and wait for token.
 * This implementation only renders the Turnstile component when executeAndWaitForToken is called.
 * @param props The props to pass to the Turnstile component
 * @returns An object containing executeAndWaitForToken method
 */
export const useTurnstile = (
  props: TurnstileProps
): {
  executeAndWaitForToken: (options?: {
    keepMounted?: boolean;
    successDelayMs?: number;
    timeoutMs?: number;
  }) => Promise<string>;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  TurnstileComponent: () => JSX.Element | null;
} => {
  const [shouldRender, setShouldRender] = useState(false);
  type TokenPromise = {
    resolve: (token: string) => void;
    reject: (error: string) => void;
    active: boolean;
    keepMounted: boolean;
    successDelayMs?: number;
    // Owned by THIS call's own object (never a shared ref) so that clearing/nulling it can never
    // clobber a different, still-in-flight call's timer id - see executeAndWaitForToken.
    timeoutId: ReturnType<typeof setTimeout> | null;
  };
  const tokenPromiseRef = useRef<TokenPromise | null>(null);
  // Keep the latest props readable from inside executeAndWaitForToken's safety-net timer
  // without making executeAndWaitForToken itself depend on (and be recreated whenever) `props`
  // changes identity - its referential stability matters to callers that put it in their own
  // effect/callback dependency arrays.
  const propsRef = useRef(props);
  propsRef.current = props;

  // Clears the timer belonging to whichever call is CURRENT (tokenPromiseRef.current - the same
  // object the mounted widget is wired to). The timer id itself lives on that call's own object,
  // not a separate shared timer-id ref, so clearing it here can never cancel or lose track of a
  // different call's live timer.
  const clearSafetyTimeout = useCallback(() => {
    const { current } = tokenPromiseRef;
    if (current?.timeoutId != null) {
      clearTimeout(current.timeoutId);
      current.timeoutId = null;
    }
  }, []);

  // Once Cloudflare has actually rendered an interactive checkbox for the user, our own
  // external safety timer no longer applies - a real person may legitimately take longer than
  // TURNSTILE_TIMEOUT_MS to solve it. From this point, Cloudflare's own onTimeout (wired to
  // errorCallback below) is responsible for bounding an abandoned interactive challenge instead.
  //
  // Known gaps (see PR discussion, not fixed here):
  // - If the widget dies after becoming interactive without Cloudflare ever calling onTimeout
  //   (network drop, backgrounded tab), nothing currently bounds the promise.
  // - This only fires once per widget mount. A retry that reuses an already-solved, still-
  //   mounted widget (keepMounted: true) does not get a fresh interactive event, and more
  //   fundamentally does not currently cause the widget to re-challenge at all (no ref-based
  //   .execute()/.reset() call exists anywhere in this file) - a deeper, pre-existing gap in
  //   how retries work that this timeout fix does not attempt to solve.
  const beforeInteractiveCallback = useCallback(() => {
    props.onBeforeInteractive?.();
    clearSafetyTimeout();
  }, [props, clearSafetyTimeout]);

  // Success callback wrapper
  const successCallback = useCallback(
    (token: string) => {
      // Call the original callback
      props.onSuccess?.(token);

      // Resolve the promise if it exists
      const settledPromise = tokenPromiseRef.current;
      if (settledPromise?.active) {
        const delayMs = settledPromise.successDelayMs ?? 1000;
        const { resolve, keepMounted: shouldKeepMounted } = settledPromise;

        // Mark inactive immediately so the timeout guard cannot fire after this point
        settledPromise.active = false;
        clearSafetyTimeout();

        // Use setTimeout for both immediate and delayed resolution
        setTimeout(() => {
          resolve(token);

          // Unmount the component after getting the token only if keepMounted is false AND this
          // call is still the one the mounted widget belongs to - a new executeAndWaitForToken()
          // call may have started during this delay and must not be torn down by a stale unmount
          // from a call that already resolved.
          if (!shouldKeepMounted && tokenPromiseRef.current === settledPromise) {
            setShouldRender(false);
          }
        }, delayMs);
      }
    },
    [props, clearSafetyTimeout]
  );

  // Error callback wrapper
  const errorCallback = useCallback(
    (errorMsg: string) => {
      // Call the original callback
      props.onError?.(errorMsg);

      // Reject the promise if it exists
      if (tokenPromiseRef.current?.active) {
        tokenPromiseRef.current.reject(errorMsg);
        tokenPromiseRef.current.active = false;
        clearSafetyTimeout();

        // Unmount the component after getting an error
        setShouldRender(false);
      }
    },
    [props, clearSafetyTimeout]
  );

  // Method to execute the challenge and return a promise
  const executeAndWaitForToken = useCallback(
    (options?: { keepMounted?: boolean; successDelayMs?: number; timeoutMs?: number }): Promise<string> =>
      new Promise<string>((resolve, reject) => {
        const timeout = options?.timeoutMs ?? TURNSTILE_TIMEOUT_MS;

        // Store the promise handlers. Kept as a local reference (not just read back off
        // tokenPromiseRef) so the timeout below always settles THIS invocation's own promise via
        // pendingPromise.reject, never whatever happens to be current on tokenPromiseRef - a
        // superseded, still-active call must still get a chance to settle on its own schedule
        // rather than hang forever just because a later retry took over the shared ref.
        const pendingPromise: TokenPromise = {
          resolve,
          reject,
          active: true,
          keepMounted: options?.keepMounted ?? false,
          successDelayMs: options?.successDelayMs,
          timeoutId: null,
        };
        tokenPromiseRef.current = pendingPromise;

        // Render the component first
        setShouldRender(true);

        // Belt-and-suspenders: if the Turnstile script/widget never calls back at all (e.g. the
        // script request hangs with no onload/onerror ever firing, which browsers don't time out
        // on their own), this bounds how long a caller can be left awaiting the token so a spinner
        // can't hang forever pending a manual page refresh. This is cleared as soon as the widget
        // proves it loaded (onBeforeInteractive) or the call settles, so it never fires against a
        // user who is actively (if slowly) solving a real interactive challenge. The timer id is
        // stored on pendingPromise itself (never a shared ref), so a later call's timer can never
        // be clobbered by this one finishing/clearing.
        pendingPromise.timeoutId = setTimeout(() => {
          pendingPromise.timeoutId = null;

          if (!pendingPromise.active) {
            return;
          }

          // Match every other failure path (onError, onTimeout, scriptOptions.onError) in
          // logging and forwarding to the caller's onError - otherwise this is the one failure
          // route with no signal for telemetry/logging, and no way to measure how often this
          // safety net actually trips in production.
          const timeoutErrorMsg = 'Turnstile challenge timed out';
          console.error('Turnstile error', timeoutErrorMsg);
          propsRef.current.onError?.(timeoutErrorMsg);

          pendingPromise.reject(timeoutErrorMsg);
          pendingPromise.active = false;

          // Only unmount the widget if it's still serving THIS call - a later, still-in-flight
          // retry may have superseded this one and must not be torn out from under it.
          if (tokenPromiseRef.current === pendingPromise) {
            setShouldRender(false);
          }
        }, timeout);
      }),
    []
  );

  // Render the Turnstile component only when needed
  const TurnstileComponent = useCallback((): JSX.Element | null => {
    if (!shouldRender) {
      return null;
    }

    return (
      <Turnstile
        {...props}
        onSuccess={successCallback}
        onError={errorCallback}
        onBeforeInteractive={beforeInteractiveCallback}
      />
    );
  }, [props, successCallback, errorCallback, beforeInteractiveCallback, shouldRender]);

  return {
    executeAndWaitForToken,
    TurnstileComponent,
  };
};

export default Turnstile;

const TurnstileContainer = styled.div<{ $turnstileIsInteractive?: boolean }>`
  margin: ${(props) => (props.$turnstileIsInteractive ? '1rem 0' : '0')};
  visibility: ${(props) => (props.$turnstileIsInteractive ? 'visible' : 'hidden')};
  display: ${(props) => (props.$turnstileIsInteractive ? 'flex' : 'none')};
  justify-content: center;
  align-items: center;
`;
